<template>
    <slot v-bind="{
        visibleMonth,
        months,
        hasPrevious,
        hasNext,
        previousMonth,
        nextMonth,
        showSingleMonth: props.showSingleMonth,
        setSelectedDate: props.setSelectedDate,
    }" />
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import zonedDate from '../utils/zoned-date';
import { addDays, eachMonthOfInterval, endOfMonth, endOfWeek, format, isBefore, isSameDay, isSameMonth, max, min, startOfMonth, startOfWeek } from 'date-fns';

const props = defineProps({
    items: {
        type: Array,
        required: true,
    },
    showSingleMonth: {
        type: Boolean,
        default: false,
    },
    setSelectedDate: {
        type: Function,
        required: true,
    },
});

const currentMonthIndex = ref(0);

const months = computed(() => {
    const today = zonedDate();

    const dates = props.items.map(instance => {
        return instance.startDate;
    });

    const firstEvent = dates.length ? min(dates) : today;
    const lastEvent = dates.length ? max(dates) : today;

    return eachMonthOfInterval({
        start: startOfMonth(firstEvent),
        end: endOfMonth(lastEvent),
    }).map(month => {
        const monthStart = startOfMonth(month);
        const monthEnd = endOfMonth(month);

        const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday
        const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

        const weeks = [];
        let current = calendarStart;
        let week = [];

        while (current <= calendarEnd) {
            const isInMonth = isSameMonth(current, month);
            const hasItems = isInMonth && dates.find(date => isSameDay(current, date));
            const isPast = !hasItems && isBefore(current, today) && !isSameDay(current, today);
            const isToday = isSameDay(current, today);

            week.push({
                date: current,
                day: format(current, 'd'),
                id: `day-${format(current, 'yyyy-MM-dd')}`,
                isPast,
                isToday,
                isInMonth,
                hasItems,
                title: format(current, 'EEEE MMMM do, yyyy'),
            });

            if (week.length === 7) {
                weeks.push(week);
                week = [];
            }

            current = addDays(current, 1);
        }

        return {
            id: `month-${format(month, 'yyyy-MM')}`,
            title: format(month, 'MMMM yyyy'),
            weeks,
        }
    });
});

const visibleMonth = computed(() => {
    if (!months.value || !months.value.length) {
        return null;
    }

    return months.value[currentMonthIndex.value];
});

const hasPrevious = computed(() => {
    return currentMonthIndex.value > 0;
});

const hasNext = computed(() => {
    return currentMonthIndex.value < months.value.length - 1;
});

const previousMonth = () => {
    if (hasPrevious.value) {
        currentMonthIndex.value--;
    }
}

const nextMonth = () => {
    if (hasNext.value) {
        currentMonthIndex.value++;
    }
}

watch(months, newMonths => {
    if (!!newMonths || newMonths.length) {
        return;
    }

    const todayIndex = newMonths.findIndex(month => {
        return month.weeks.some(week => {
            return week.some(day => {
                return day.isToday;
            });
        });
    });

    currentMonthIndex.value = todayIndex !== -1 ? todayIndex : 0;
}, { immediate: true });

defineExpose({
    goToMonth(id) {
        if (!props.showSingleMonth) {
            return;
        }

        const index = months.value.findIndex(item => {
            return item.id === id;
        });

        if (index !== -1 && currentMonthIndex.value !== index) {
            currentMonthIndex.value = index;
        }
    },
});
</script>
